import React, { ChangeEvent, forwardRef, SVGProps, useEffect, useState } from 'react';
import {
  getCountryCallingCode,
  getCountries,
  // isValidPhoneNumber as validatePhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input';
// Calling this function from /mobile to only allow mobile phone numbers and not landline/fixed numbers
import { isValidPhoneNumber as validatePhoneNumber } from 'react-phone-number-input/mobile';
import countriesList from './assets/countries.json';
import HandIcon from './assets/HandIcon';
import { TextInput as FlowBiteTextInput } from 'flowbite-react';
import { MenuSelector, MenuSelectorItemProps } from '../menu-selector';
import { cn } from '@restworld/utils-common';

/**
 * It is used to fetch the country flag
 *
 */

export function getCountryFlagUrl(countryShort: string) {
  return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryShort}.svg`;
}
/**
 *
 * It is used to create the countries data
 */

function getCountriesData(): CountryData[] {
  const data = getCountries().map((country) => {
    const countryShort: CountryShort = country as CountryShort;
    const countryName = countriesList[countryShort];
    return {
      countryName,
      countryShort: country,
      countryCode: getCountryCallingCode(country),
      flag: <img src={getCountryFlagUrl(countryShort)} width={20} height={14} alt={country} />,
    };
  });

  return data;
}

export function isValidPhoneNumber(phoneNumber: string): boolean {
  return phoneNumber ? validatePhoneNumber(phoneNumber) : false;
}

export const countriesData = getCountriesData();

export interface CountryData {
  countryName?: string;
  countryCode: string;
  countryShort: string;
  flag: React.ReactNode;
}

export type CountryShort = keyof typeof countriesList;

export type PhoneInputVariant = 'primary' | 'error';

export interface PhoneInputProps {
  /**
   * The phone number value with a country without spaces
   */
  value?: string;
  /**
   * Text label
   */
  label?: string | React.ReactNode;

  /**
   * It is used to choose the variant of the input
   */
  variant?: PhoneInputVariant;

  /**
   * It is  used to display the support text
   *
   * A node, not only a string, because an error can carry an action: "this number already
   * belongs to an account, log in" needs the link inside the message. Same as `label` above,
   * and as the support text of the e-mail input.
   */
  supportText?: string | React.ReactNode;

  /**
   * It is used to display the input placehold
   */
  placeholder?: string;

  /**
   * It is used to set the default country
   */
  defaultCountry?: CountryShort;

  /**
   * It is used to display most used countries
   */
  priorityCountries?: CountryShort[];

  /**
   *
   * It is used to capture phone input change
   */
  onChange: (value: string) => void;
  /**
   *
   * It is used to format the displayed phone number in the input default 000 0000000
   */
  formatDisplayedNumber?: (value: string) => string;
  /**
   * It is used to style the label
   */
  labelStyles?: string[];
  /**
   * It is used to style the input container
   */
  inputContainerStyles?: string[];
  /**
   * It is used to style select button
   */
  selectButtonStyles?: string[];
  /**
   * It is used to style the menu dialog
   */
  menuDialogStyles?: string[];
  /**
   * It is is to style the number input
   */
  numberInputStyles?: string[];
  /**
   * It is used to style the support text
   */
  supportTextStyles?: string;
  /**
   * Suffix icon
   */
  suffixIcon?: React.FC<SVGProps<SVGSVGElement>>;
  /**
   * It is used to disable the phone input
   */
  disabled?: boolean;
  /**
   * It is used to apply testIDs the phone input
   */
  testIds?: {
    input: string;
    dropdownBtn?: string;
    dropdownOptions?: string;
    supportText?: string;
  };
  /**
   * It is used to set the id of the phone input
   */
  id?: string;
  /**
   * callback function to be called when the input loses focus
   */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const PhoneInput = forwardRef(
  (
    {
      value,
      onChange,
      variant = 'primary',
      defaultCountry = 'IT',
      label,
      supportText,
      placeholder,
      labelStyles,
      inputContainerStyles,
      selectButtonStyles,
      menuDialogStyles,
      suffixIcon,
      supportTextStyles,
      formatDisplayedNumber,
      priorityCountries,
      disabled,
      testIds,
      id,
      onBlur,
    }: PhoneInputProps,
    ref
  ) => {
    const [activeCountry, setActiveCountry] = useState<CountryData | null>(null);
    const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);

    useEffect(() => {
      if (defaultCountry && activeCountry === null) {
        const selectedCountry: CountryData | undefined = countriesData.find(
          (country) => country.countryShort.toString() === defaultCountry
        );
        if (selectedCountry) {
          setActiveCountry(selectedCountry);
        }
      }
    }, [activeCountry, defaultCountry]);

    // Set phone number default value
    useEffect(() => {
      const parsedPhoneNumber = parsePhoneNumber(value || '', 'IT');
      if (parsedPhoneNumber && (!activeCountry || !phoneNumber)) {
        const countryCode = parsedPhoneNumber.country;
        const selectedCountry: CountryData | undefined = countriesData.find(
          (country) => country.countryShort.toString() === countryCode
        );
        if (selectedCountry) {
          setActiveCountry(selectedCountry);
        }
        const formattedValue = parsedPhoneNumber.nationalNumber.toString();
        const newValue = formatDisplayedNumber
          ? formatDisplayedNumber(formattedValue)
          : formatPhoneNumber(formattedValue);
        setPhoneNumber(newValue);
      }
    }, [activeCountry, formatDisplayedNumber, phoneNumber, value]);

    function onTextFieldChange(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
      const value: string = event.target.value;
      const formattedValue = getDigits(value);
      const newValue = formatDisplayedNumber
        ? formatDisplayedNumber(formattedValue)
        : formatPhoneNumber(formattedValue);
      setPhoneNumber(newValue);
      onChange(`+${activeCountry?.countryCode || ''}${formattedValue}`);
    }

    function onCountrySelectChange(countryShort: string) {
      const selectedCountry: CountryData | undefined = countriesData.find(
        (country) => country.countryShort.toString() === countryShort
      );

      if (selectedCountry) {
        setActiveCountry(selectedCountry);
        onChange(`+${selectedCountry.countryCode}${getDigits(phoneNumber || '')}`);
      }
    }

    function getActiveCountryLabel(activeCountry: CountryData | null): React.ReactNode | undefined {
      if (!activeCountry) {
        return;
      }
      return (
        <div className="inline-flex flex-row gap-2">
          {activeCountry.flag}
          <p>+{`${activeCountry.countryCode}`}</p>
        </div>
      );
    }

    const countryOptions: MenuSelectorItemProps[] = countriesData.map(
      ({ countryName, countryCode, countryShort, flag }) => ({
        label: (
          <div className="flex flex-row gap-2 items-center">
            {' '}
            <div>{flag}</div>{' '}
            <p>
              {countryName} (+{countryCode})
            </p>{' '}
          </div>
        ),
        value: countryShort,
      })
    );

    function formatPhoneNumber(phoneNumber: string) {
      if (phoneNumber.length > 3) {
        return phoneNumber.slice(0, 3) + ' ' + phoneNumber.slice(3);
      }
      return phoneNumber;
    }

    function getDigits(value: string) {
      return value.replace(/\D/g, '');
    }

    const isErrored = variant === 'error';

    return (
      <div className="flex flex-col w-full">
        {label && <StyledInputLabel labelStyles={labelStyles}>{label}</StyledInputLabel>}
        <StyledInputContainer inputContainerStyles={inputContainerStyles} errored={isErrored}>
          <MenuSelector
            options={countryOptions}
            menuDialogStyles={generateMenuDialogStyles(menuDialogStyles)}
            menuButtonStyles={generateMenuButtonStyles(isErrored, selectButtonStyles)}
            onSelect={(value) => onCountrySelectChange(value.toString())}
            selectedLabel={getActiveCountryLabel(activeCountry)}
            priorityItems={priorityCountries}
            disabled={disabled}
            testIds={{ dropdownBtn: testIds?.dropdownBtn, dropdownOptions: testIds?.dropdownOptions }}
          />
          <div className="flex flex-1 pr-2">
            <TextField
              variant={variant}
              onChange={onTextFieldChange}
              value={phoneNumber}
              placeholder={placeholder || '000 0000000'}
              rightIcon={(isErrored && HandIcon) || (suffixIcon && suffixIcon) || undefined}
              disabled={disabled}
              data-cy={testIds?.input || 'phone-number-input'}
              id={id}
              onBlur={onBlur}
            />
          </div>
        </StyledInputContainer>

        {(supportText || variant === 'error') && (
          <StyledSupportText variant={variant} supportTextStyles={supportTextStyles} data-cy={testIds?.supportText}>
            {supportText ? supportText : variant === 'error' ? 'Il numero di telefono non è valido' : ''}
          </StyledSupportText>
        )}
      </div>
    );
  }
);

const TextField = ({ variant, className, ...props }: any) => (
  <FlowBiteTextInput
    className={cn(
      'flex flex-1 !bg-white',
      '[&_input]:outline-none [&_input]:rounded-none [&_input]:border-none [&_input]:font-normal [&_input]:text-base [&_input]:text-black [&_input]:leading-5 [&_input]:!bg-white [&_input]:pl-4 [&_input]:pr-2',
      variant === 'primary' && '[&_input]:placeholder:text-gray-400',
      variant === 'error' && '[&_input]:!bg-red-50 [&_input]:placeholder:text-red-600',
      className
    )}
    {...props}
  />
);

const StyledInputLabel = ({ labelStyles, className, ...props }: any) => (
  <p className={cn('mb-2 text-base leading-6 font-medium text-black', className)} {...props} />
);

const StyledInputContainer = ({ errored, inputContainerStyles, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-row items-center justify-start w-full gap-0 border-y border-r border-solid border-gray-200 bg-white box-border rounded-lg',
      errored && '!border-red-600 !bg-red-50',
      className
    )}
    {...props}
  />
);

const generateMenuDialogStyles = (menuDialogStyles?: string[]) => [
  'max-h-menu overflow-hidden !w-85.75 overflow-y-scroll',
  ...(menuDialogStyles || []),
];

const generateMenuButtonStyles = (errored: boolean, menuButtonStyles?: string[]) => {
  const styles = [
    'border-0 border-r border-l border-solid  rounded-l-lg border-l-gray-200  border-r-gray-200 bg-gray-50',
    ...(menuButtonStyles || []),
  ];
  if (errored) {
    styles.push('!border-r-red-600 !bg-red-50 !border-l-red-600');
  }
  return styles;
};

const StyledSupportText = ({ variant, supportTextStyles, className, ...props }: any) => (
  <p
    className={cn(
      'mt-3 text-sm leading-5.25 font-normal',
      variant === 'primary' && 'text-gray-500',
      variant === 'error' && 'text-red-600',
      className
    )}
    {...props}
  />
);
